import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import {
  InsertDriveFileOutlined,
  PictureAsPdfOutlined,
  DescriptionOutlined,
  TableChartOutlined,
  VideoFileOutlined,
  AudioFileOutlined,
  CodeOutlined,
  ArchiveOutlined,
  ImageOutlined,
  SlideshowOutlined
} from '@mui/icons-material';

export interface FileTypeIconProps {
  file: File;
  size?: number;
  sx?: any;
}

export interface FileThumbnailProps {
  file: File;
  width?: number;
  height?: number;
  sx?: any;
}

export const FileThumbnail: React.FC<FileThumbnailProps> = ({
  file,
  width = 100,
  height = 100,
  sx
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const thumbnailUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const generateThumbnail = async () => {
      try {
        const fileType = file.type.toLowerCase();

        if (fileType.startsWith('image/')) {
          const url = URL.createObjectURL(file);
          setThumbnailUrl(url);
          thumbnailUrlRef.current = url;
          setIsLoading(false);
          return;
        }

        if (fileType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          setIsLoading(false);
          return;
        }

        if (fileType.startsWith('video/')) {
          const video = document.createElement('video');
          video.src = URL.createObjectURL(file);
          video.currentTime = 1;

          video.addEventListener('loadeddata', () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
              canvas.width = width;
              canvas.height = height;
              ctx.drawImage(video, 0, 0, width, height);
              const thumbnailUrl = canvas.toDataURL();
              setThumbnailUrl(thumbnailUrl);
              thumbnailUrlRef.current = thumbnailUrl;
              setIsLoading(false);
            }
          });

          video.addEventListener('error', () => {
            setIsLoading(false);
          });

          return;
        }

        if (fileType.includes('excel') || fileType.includes('csv') ||
          file.name.toLowerCase().endsWith('.xls') ||
          file.name.toLowerCase().endsWith('.xlsx') ||
          file.name.toLowerCase().endsWith('.csv')) {
          setIsLoading(false);
          return;
        }

        if (fileType.includes('word') ||
          file.name.toLowerCase().endsWith('.doc') ||
          file.name.toLowerCase().endsWith('.docx')) {
          setIsLoading(false);
          return;
        }

        if (fileType.includes('presentation') ||
          file.name.toLowerCase().endsWith('.ppt') ||
          file.name.toLowerCase().endsWith('.pptx')) {
          setIsLoading(false);
          return;
        }

        if (fileType.startsWith('text/') ||
          file.name.toLowerCase().endsWith('.txt') ||
          file.name.toLowerCase().endsWith('.md') ||
          file.name.toLowerCase().endsWith('.json') ||
          file.name.toLowerCase().endsWith('.xml')) {
          setIsLoading(false);
          return;
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error generating thumbnail:', error);
        setIsLoading(false);
      }
    };

    generateThumbnail();

    return () => {
      if (thumbnailUrlRef.current) {
        URL.revokeObjectURL(thumbnailUrlRef.current);
      }
    };
  }, [file, width, height]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          borderRadius: '8px',
          ...sx
        }}
      >
        <Typography variant="caption">Loading...</Typography>
      </Box>
    );
  }

  if (thumbnailUrl) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          ...sx
        }}
      >
        <img
          src={thumbnailUrl}
          alt={file.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'grey.50',
        borderRadius: '12px',
        p: 1,
        border: '2px solid',
        borderColor: 'grey.200',
        position: 'relative',
        overflow: 'hidden',
        ...sx
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight: 0
        }}
      >
        {getFileTypeIcon(file, Math.min(width, height) * 0.5)}
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            textAlign: 'center',
            wordBreak: 'break-word',
            fontSize: '0.7rem',
            lineHeight: 1.2,
            color: 'text.primary',
            fontWeight: 500,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {file.name.length > 10 ? file.name.substring(0, 10) + '...' : file.name}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.6rem',
          color: 'text.disabled',
          position: 'absolute',
          bottom: 4,
          right: 6,
          backgroundColor: 'rgba(255,255,255,0.8)',
          px: 0.5,
          py: 0.25,
          borderRadius: '4px'
        }}
      >
        {(file.size / 1024).toFixed(0)}KB
      </Typography>
    </Box>
  );
};

export const getFileTypeIcon = (file: File, size: number = 40, sx?: any) => {
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  // Image files
  if (fileType.startsWith('image/')) {
    return <ImageOutlined sx={{ fontSize: size, ...sx }} />;
  }

  // PDF files
  if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return <PictureAsPdfOutlined sx={{ fontSize: size, color: '#dc3545', ...sx }} />;
  }

  // Word documents
  if (fileType.includes('word') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
    return <DescriptionOutlined sx={{ fontSize: size, color: '#007bff', ...sx }} />;
  }

  // Excel/CSV files
  if (fileType.includes('excel') || fileType.includes('csv') ||
    fileName.endsWith('.xls') || fileName.endsWith('.xlsx') || fileName.endsWith('.csv')) {
    return <TableChartOutlined sx={{ fontSize: size, color: '#28a745', ...sx }} />;
  }

  // PowerPoint files
  if (fileType.includes('presentation') || fileName.endsWith('.ppt') || fileName.endsWith('.pptx')) {
    return <SlideshowOutlined sx={{ fontSize: size, ...sx }} />;
  }

  // Video files
  if (fileType.startsWith('video/')) {
    return <VideoFileOutlined sx={{ fontSize: size, ...sx }} />;
  }

  // Audio files
  if (fileType.startsWith('audio/')) {
    return <AudioFileOutlined sx={{ fontSize: size, ...sx }} />;
  }

  // Archive files
  if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('7z') ||
    fileName.endsWith('.zip') || fileName.endsWith('.rar') || fileName.endsWith('.7z')) {
    return <ArchiveOutlined sx={{ fontSize: size, ...sx }} />;
  }

  // Code files
  if (fileName.endsWith('.js') || fileName.endsWith('.ts') || fileName.endsWith('.jsx') ||
    fileName.endsWith('.tsx') || fileName.endsWith('.html') || fileName.endsWith('.css') ||
    fileName.endsWith('.py') || fileName.endsWith('.java') || fileName.endsWith('.cpp') ||
    fileName.endsWith('.c') || fileName.endsWith('.php') || fileName.endsWith('.rb') ||
    fileName.endsWith('.go') || fileName.endsWith('.rs') || fileName.endsWith('.swift')) {
    return <CodeOutlined sx={{ fontSize: size, ...sx }} />;
  }

  // Default file icon
  return <InsertDriveFileOutlined sx={{ fontSize: size, ...sx }} />;
};

export const FileTypeIcon: React.FC<FileTypeIconProps> = ({ file, size = 40, sx }) => {
  return getFileTypeIcon(file, size, sx);
};
